import React, { useState } from 'react';
import { StyleProp, TextStyle, View } from 'react-native';
import MyAppText from './MyAppText';
import { UnistylesRuntime } from 'react-native-unistyles';
import DatePicker from 'react-date-picker';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import '../styles/date-picker.css';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { translate } from '../services/translate';
import { Hoverable } from 'react-native-web-hover';
import { CustomTooltip } from './CustomTooltip';

type ValuePiece = Date | null;
export type CalendarValue = ValuePiece;
type MyDateTimePickerProps = {
    label: string;
    editable?: boolean;
    maxDate?: Date;
    minDate?: Date;
    disabled?: boolean;
    zIndex?: number;
    labelFontSize?: number;
    onCalendarOpen?: () => void;
    onCalendarClose?: () => void;
    required?: boolean;
    labelStyle?: StyleProp<TextStyle>;
    viewStyle?: StyleProp<TextStyle>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    dateFieldName: string;
    date?: undefined;
    onChangeDate?: undefined;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any, form: UseFormReturn<any, any, any>) => string | true;
    };
    linkedFields?: string[];
    hintText?: string | React.ReactNode;
    hintTextConfig?: {
        tooltipStyle?: React.CSSProperties;
        tooltipDirection?: 'right' | 'left' | 'top';
    };

};

export default function MyDatePicker({
    label,
    minDate,
    maxDate,
    disabled,
    zIndex,
    labelFontSize,
    onCalendarOpen,
    onCalendarClose,
    required,
    labelStyle,
    viewStyle,
    form,
    dateFieldName,
    customValidations,
    linkedFields,
    hintText,
    hintTextConfig
}: MyDateTimePickerProps) {
    const { styles, theme } = useStyles(styleSheet);
    const localMinDate = minDate ? minDate : new Date(1900, 0, 1);

    const [hintHovered, setHintHovered] = useState(false);

    const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

    if (required) {
        rules.required = translate('required_field');
    }

    if (customValidations) {
        rules.validate = Object.fromEntries(
            Object.entries(customValidations).map(([key, fn]) => [
                key,
                (value: unknown) => fn(value, form)
            ])
        );
    }

    return (
        <View style={[hintHovered && { zIndex: 9999 }, viewStyle ?? { rowGap: 5, flex: 1 }, zIndex ? { zIndex } : {}]}>
            <View style={[{ flexDirection: 'row', gap: 3, alignItems: 'center' }, hintText ? { zIndex: 9999 } : {}]}>
                <MyAppText
                    style={labelStyle ?? styles.label(labelFontSize)}>
                    {label}
                </MyAppText>
                {required ?
                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                    : null}
                {hintText && (
                    <Hoverable>
                        {({ hovered }) => {
                            setHintHovered(hovered);
                            return <CustomTooltip
                                content={hintText}
                                tooltipStyle={hintTextConfig?.tooltipStyle}
                                tooltipDirection={hintTextConfig?.tooltipDirection}
                            />;
                        }}
                    </Hoverable>
                )}
            </View>
            <View style={[styles.container(undefined), { flex: 1 }]}>
                <Controller
                    control={form.control}
                    name={dateFieldName}
                    rules={rules}
                    render={({ field, fieldState }) => (
                        <>
                            <DatePicker
                                calendarProps={{ locale: 'pt-BR' }}
                                maxDate={maxDate}
                                minDate={localMinDate}
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                onChange={(val) => {
                                    field.onChange(val);
                                    if (linkedFields?.length) {
                                        form.trigger(linkedFields);
                                    }
                                }}
                                autoFocus={false}
                                shouldOpenCalendar={({ reason }) => reason !== 'focus'}
                                onCalendarOpen={onCalendarOpen}
                                onCalendarClose={onCalendarClose}
                                value={field.value}
                                clearIcon={null}
                                locale='pt-BR'
                                className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight${disabled ? 'Disabled' : ''}`] : ['inputColor', `inputColorDark${disabled ? 'Disabled' : ''}`]}
                                calendarIcon={<FontAwesomeIcon icon={faCalendar as IconDefinition} color={theme.colors.neutral['400']}></FontAwesomeIcon>}
                                disabled={disabled}
                            />
                            {fieldState.error ? <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText> : null}
                        </>
                    )}
                />
            </View>
        </View>
    );
}


const styleSheet = createStyleSheet((theme) => ({
    label: (fontSize) => ({
        color: theme.colors.labelColor,
        fontSize,
    }),
    container: (zIndex?: number) => ({
        zIndex,
        '--daterange-invalid-border-color': theme.colors.errors['500'],
        '--daterange-text-color': theme.colors.textColor,
        '--daterange-border-color': theme.colors.field.border,
        '--daterange-button-color': theme.colors.iconColor,

        '--daterange-calendar-background-color': theme.colors.calendar.background,
        '--daterange-calendar-border-color': theme.colors.calendar.border,
        '--daterange-calendar-days-text-color': theme.colors.calendar.dayText,
        '--daterange-calendar-navigation-text-color': theme.colors.calendar.navigationText,
        '--daterange-calendar-navigation-text-disabled-color': theme.colors.calendar.navigationDisabledText,
        '--daterange-calendar-navigation-background-hover-color': theme.colors.calendar.navigationBackgroundHover,
        '--daterange-calendar-days-background-selected-active-color': theme.colors.calendar.dayBackgroundSelectedActive,
        '--daterange-calendar-days-background-selected-range-color': theme.colors.calendar.dayBackgroundSelectedRange,
        '--daterange-calendar-days-text-selected-range-color': theme.colors.calendar.dayTextSelectedRangeColor,
        '--daterange-calendar-days-text-disabled-color': theme.colors.calendar.dayTextDisabledColor,
    }),
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10
    },
}));
